import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Sparkles, Loader2, ShieldAlert, Wrench, Clock, Scale, TrendingUp, Coins, Users, Bot, History, AlertTriangle, RefreshCw, FileDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RISK_META = {
  iyi: { label: 'İyi', bg: 'bg-emerald-600', text: 'text-emerald-300', ring: 'ring-emerald-500/40' },
  izle: { label: 'İzle', bg: 'bg-yellow-500', text: 'text-yellow-300', ring: 'ring-yellow-500/40' },
  riskli: { label: 'Riskli', bg: 'bg-orange-500', text: 'text-orange-300', ring: 'ring-orange-500/40' },
  kritik: { label: 'Kritik', bg: 'bg-red-600', text: 'text-red-300', ring: 'ring-red-500/40' },
};
const SEV = {
  kritik: 'bg-red-600 text-white', yuksek: 'bg-orange-500 text-white', orta: 'bg-amber-500 text-gray-950', dusuk: 'bg-gray-600 text-white',
};
const KAT = { guvenlik: 'Güvenlik', yasal: 'Yasal Uyum', performans: 'Performans', maliyet: 'Maliyet', kozmetik: 'Kozmetik' };
const AKSIYON = { izle: 'İzle', bakim_planla: 'Bakım Planla', onar: 'Onar', degistir: 'Değiştir', acil_mudahale: 'Acil Müdahale' };
const BANT = { dusuk: 'Düşük', orta: 'Orta', yuksek: 'Yüksek' };
const TREND = { artiyor: 'Artıyor', stabil: 'Stabil', azaliyor: 'Azalıyor', veri_yok: 'Veri yok' };

export const SkorRozeti = ({ score, level, size = 'sm' }) => {
  if (score === null || score === undefined) return <span className="text-[10px] text-gray-500 border border-gray-700 rounded px-1.5 py-0.5">Analiz yok</span>;
  const m = RISK_META[level] || RISK_META.izle;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${m.bg} text-white font-bold ${size === 'lg' ? 'text-base px-3 py-1' : 'text-[11px] px-2 py-0.5'}`} data-testid="skor-rozeti">
      {score}<span className="opacity-80 font-normal">/100</span> · {m.label}
    </span>
  );
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }) : '-';

/** Ekipman detayı — "Uzman Görüşü" paneli (Sheet) */
export default function EkipmanZekasiDetay({ ekipman, open, onOpenChange, onAnalyzed }) {
  const [insight, setInsight] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const { user } = useAuth();
  const isViewer = !!user?.is_viewer && !user?.is_admin;

  const load = useCallback(async () => {
    if (!ekipman?.id) return;
    setLoading(true);
    try {
      const [l, h] = await Promise.all([
        axios.get(`${API}/ai/insights/${ekipman.id}/latest`, { withCredentials: true }),
        axios.get(`${API}/ai/insights/${ekipman.id}/history`, { withCredentials: true }),
      ]);
      setInsight(l.data.exists ? l.data : null);
      setHistory(h.data || []);
    } catch (e) { toast.error('Analiz bilgisi yüklenemedi'); }
    finally { setLoading(false); }
  }, [ekipman?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const analyze = async () => {
    if (insight && insight.data_changed === false) {
      if (!window.confirm('Son analizden bu yana ekipman verisi değişmedi. Yine de yeniden analiz edilsin mi? (LLM çağrısı yapılır)')) return;
    }
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/ai/analyze/${ekipman.id}?force=true`, {}, { withCredentials: true });
      toast.success(`Uzman analizi tamamlandı · ${res.data.agent_name}`);
      await load();
      onAnalyzed?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analiz başarısız');
    } finally { setAnalyzing(false); }
  };

  const okHistory = history.filter(h => h.status === 'ok').slice().reverse();
  const chartData = okHistory.map(h => ({ t: new Date(h.analyzed_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }), skor: h.health_score, taban: Math.round(h.base_health_score) }));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-[#0F1419] border-gray-800 text-gray-200 overflow-y-auto p-0" data-testid="ekipman-zekasi-detay">
        <SheetHeader className="p-5 border-b border-gray-800 sticky top-0 bg-[#0F1419] z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-white text-base flex items-center gap-2">
                <Bot className="w-4 h-4 text-[#E67E22]" /> Uzman Görüşü
              </SheetTitle>
              <p className="text-xs text-gray-400 mt-1 truncate">{ekipman?.alt_tip || ekipman?.ekipman_tipi} · {ekipman?.marka} {ekipman?.model} · <span className="font-mono">{ekipman?.seri_no}</span> · {ekipman?.bina_adi}</p>
              {insight && <p className="text-[10px] text-gray-500 mt-0.5">{insight.agent_name} · {insight.model} · {fmtDate(insight.analyzed_at)}{insight.data_changed ? ' · veri değişti' : ''}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
            {insight && !isViewer && (
              <Button variant="outline" onClick={() => window.open(`${API}/ai/insights/${ekipman.id}/pdf`, '_blank')} className="h-9 border-gray-600 text-gray-200 hover:bg-gray-800" title="Kurumsal PDF raporu indir" data-testid="detay-pdf">
                <FileDown className="w-4 h-4 mr-1" /> PDF
              </Button>
            )}
            {!isViewer && (
            <Button onClick={analyze} disabled={analyzing} className="bg-[#E67E22] hover:bg-[#d3701c] text-white h-9 flex-shrink-0" data-testid="detay-analiz-et">
              {analyzing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analiz ediliyor…</> : <><Sparkles className="w-4 h-4 mr-1" /> Analiz Et</>}
            </Button>
            )}
            </div>
          </div>
        </SheetHeader>

        <div className="p-5 space-y-5">
          {loading && !insight && <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor…</div>}
          {!loading && !insight && (
            <div className="text-center py-10 text-gray-500" data-testid="detay-analiz-yok">
              <Sparkles className="w-10 h-10 mx-auto mb-3 text-gray-700" />
              <p className="text-sm">Bu ekipman için henüz uzman analizi yok.</p>
              <p className="text-xs mt-1">"Analiz Et" ile tip uzmanı ajanı çalıştırın; arıza veya kontrol raporu girişlerinde otomatik da tetiklenir.</p>
            </div>
          )}
          {insight && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <SkorRozeti score={insight.health_score} level={insight.risk_level} size="lg" />
                <div className="text-xs text-gray-400">
                  Taban skor <span className="text-gray-200 font-semibold">{Math.round(insight.base_health_score)}</span>
                  {' → '}LLM <span className="text-gray-200 font-semibold">{insight.llm_raw_health_score}</span>
                  {insight.clamped && <span className="text-amber-300 ml-1">(±{Math.abs(insight.health_score - Math.round(insight.base_health_score))} kırpıldı)</span>}
                </div>
                <div className="ml-auto text-xs text-gray-400 flex gap-3">
                  <span>Kalan ömür <b className="text-gray-200">%{insight.remaining_life_pct}</b></span>
                  <span>Efektif yaş <b className="text-gray-200">{insight.effective_age_years} yıl</b></span>
                </div>
              </div>
              {insight.score_adjustment_reason && <p className="text-[11px] text-gray-500 -mt-2 italic">{insight.score_adjustment_reason}</p>}

              <section className="rounded-lg border border-gray-800 bg-gray-900/60 p-4" data-testid="detay-ozet">
                <h4 className="text-xs uppercase tracking-wider text-[#E67E22] mb-2 flex items-center gap-1.5"><Bot className="w-3.5 h-3.5" /> Yönetici Özeti</h4>
                <p className="text-sm text-gray-200 leading-relaxed">{insight.summary_tr}</p>
              </section>

              {insight.fleet_alert_msg && (
                <div className="rounded-lg border border-purple-800/50 bg-purple-900/20 p-3 text-xs text-purple-200 flex items-start gap-2" data-testid="detay-filo-uyari">
                  <Users className="w-4 h-4 mt-0.5 flex-shrink-0" /> {insight.fleet_alert_msg}
                </div>
              )}

              <section data-testid="detay-bulgular">
                <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" /> Bulgular ({insight.findings?.length || 0})</h4>
                <div className="space-y-1.5">
                  {(insight.findings || []).map((f, i) => (
                    <div key={i} className="flex items-start gap-2 rounded-md bg-gray-900/70 border border-gray-800 p-2.5">
                      <Badge className={`text-[10px] flex-shrink-0 ${SEV[f.severity] || SEV.dusuk}`}>{f.severity.toUpperCase()}</Badge>
                      <span className="text-[10px] text-cyan-300/90 flex-shrink-0 mt-0.5 w-20">{KAT[f.category] || f.category}</span>
                      <p className="text-xs text-gray-200 leading-relaxed">{f.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section data-testid="detay-oneriler">
                <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Öneriler</h4>
                <div className="space-y-1.5">
                  {(insight.recommendations || []).map((r, i) => (
                    <div key={i} className="rounded-md bg-gray-900/70 border border-gray-800 p-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="w-5 h-5 rounded-full bg-[#E67E22]/20 text-[#E67E22] text-[10px] font-bold flex items-center justify-center">{r.priority}</span>
                        <Badge className={`text-[10px] ${r.action === 'acil_mudahale' || r.action === 'degistir' ? 'bg-red-700' : r.action === 'onar' ? 'bg-orange-600' : 'bg-blue-700'} text-white`}>{AKSIYON[r.action] || r.action}</Badge>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1"><Coins className="w-3 h-3" /> {BANT[r.est_cost_band]} maliyet</span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {r.deadline_hint}</span>
                      </div>
                      <p className="text-xs text-gray-300 mt-1.5">{r.reason}</p>
                    </div>
                  ))}
                </div>
              </section>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-3">
                  <h5 className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1"><Scale className="w-3 h-3" /> Onar / Değiştir</h5>
                  <Badge className={`text-[10px] ${insight.repair_or_replace?.decision === 'degistir' ? 'bg-red-700' : insight.repair_or_replace?.decision === 'onar' ? 'bg-orange-600' : 'bg-emerald-700'} text-white`}>{AKSIYON[insight.repair_or_replace?.decision] || insight.repair_or_replace?.decision}</Badge>
                  <p className="text-gray-300 mt-1.5">{insight.repair_or_replace?.rationale}</p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-3">
                  <h5 className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1"><Coins className="w-3 h-3" /> 12 Ay Maliyet Öngörüsü</h5>
                  <Badge className="text-[10px] bg-gray-700 text-white">{BANT[insight.cost_forecast_12m?.band] || '-'}</Badge>
                  <p className="text-gray-300 mt-1.5">{insight.cost_forecast_12m?.note}</p>
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-3">
                  <h5 className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Arıza Deseni</h5>
                  <p className="text-gray-300">Trend: <b className="text-gray-100">{TREND[insight.failure_pattern?.trend] || '-'}</b> · MTBF: <b className="text-gray-100">{insight.failure_pattern?.mtbf_days ?? '-'} gün</b>{insight.failure_pattern?.systemic_issue && <span className="text-red-300 ml-1">· sistemik sorun</span>}</p>
                  {insight.failure_pattern?.recurring_root_cause && <p className="text-gray-400 mt-1">Kök neden: {insight.failure_pattern.recurring_root_cause}</p>}
                  {insight.sla_vendor_note && <p className="text-gray-400 mt-1">SLA/Yüklenici: {insight.sla_vendor_note}</p>}
                </div>
                <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-3">
                  <h5 className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1"><Scale className="w-3 h-3" /> Mevzuat Uyumu</h5>
                  <p className="text-gray-300">{insight.compliance?.periyot_uygun ? <span className="text-emerald-300">Periyot uygun</span> : <span className="text-red-300">{insight.compliance?.gecikme_gun} gün gecikmiş</span>} · Sonraki gözden geçirme: <b className="text-gray-100">{insight.next_review_date}</b></p>
                  <p className="text-gray-400 mt-1">{insight.compliance?.notlar}</p>
                </div>
              </div>

              <section className="rounded-lg border border-gray-800 bg-gray-900/60 p-3">
                <h4 className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Deterministik Bileşenler</h4>
                <div className="flex flex-wrap gap-3 text-[11px] text-gray-300">
                  {Object.entries(insight.deterministik?.bilesenler || {}).map(([k, v]) => <span key={k}>{k}=<b className="text-gray-100">{v}</b></span>)}
                  <span>Taban risk <b className="text-gray-100">{insight.base_risk_score}</b></span>
                  <span>Yoğunluk <b className="text-gray-100">{insight.deterministik?.yogunluk}</b></span>
                  <span>Yıpranma ×<b className="text-gray-100">{insight.deterministik?.yipranma_katsayisi}</b></span>
                </div>
              </section>

              {chartData.length > 1 && (
                <section data-testid="detay-skor-grafik">
                  <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Sağlık Skoru Zaman Grafiği</h4>
                  <div className="h-40 rounded-lg border border-gray-800 bg-gray-900/60 p-2">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <XAxis dataKey="t" stroke="#6b7280" fontSize={10} />
                        <YAxis domain={[0, 100]} stroke="#6b7280" fontSize={10} width={28} />
                        <Tooltip contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 11 }} />
                        <ReferenceLine y={50} stroke="#f97316" strokeDasharray="3 3" />
                        <ReferenceLine y={25} stroke="#ef4444" strokeDasharray="3 3" />
                        <Line type="monotone" dataKey="skor" name="Sağlık" stroke="#E67E22" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="taban" name="Taban" stroke="#6b7280" strokeWidth={1} strokeDasharray="4 2" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              )}
            </>
          )}

          {history.length > 0 && (
            <section data-testid="detay-gecmis">
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Geçmiş Analizler ({history.length})</h4>
              <div className="space-y-1">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] text-gray-400 border-l-2 border-gray-800 pl-2 py-1">
                    <span className="w-28 flex-shrink-0">{fmtDate(h.analyzed_at)}</span>
                    {h.status === 'ok' ? <SkorRozeti score={h.health_score} level={h.risk_level} /> : <Badge className="bg-red-900 text-red-200 text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" /> hata</Badge>}
                    <span className="truncate">{h.status === 'ok' ? h.summary_tr : h.error}</span>
                    <span className="ml-auto flex-shrink-0 text-gray-600">{h.triggered_by} · {h.key_scope} · ${h.cost_usd?.toFixed ? h.cost_usd.toFixed(3) : h.cost_usd}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
          {insight && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={load} className="text-gray-500 hover:text-gray-300 h-7 text-[11px]"><RefreshCw className="w-3 h-3 mr-1" /> Yenile</Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
